import React from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags';
import { PlatformSiteAri } from '@atlassian/ari';

import type { peopleEntityProfileRelayQuery } from './__generated__/peopleEntityProfileRelayQuery.graphql';
import { UserProfilePreviewRelay } from './lumina-user-profile-preview-relay';

function buildSiteAri(cloudId: string, sourceProduct?: string): string {
	if (sourceProduct) {
		return `ari:cloud:${sourceProduct}::site/${cloudId}`;
	}
	return cloudId;
}

/**
 * Builds the collaboration-context ARI used to scope the recent-work (`workedOn`)
 * query to the origin site, so activity from other sites the person belongs to is
 * not returned. Returns `null` when the site ID is missing or invalid so the query
 * safely falls back to unscoped behaviour.
 */
function buildCollabContextId(cloudId: string): string | null {
	if (!cloudId) {
		return null;
	}
	try {
		return PlatformSiteAri.create({ siteId: cloudId }).toString();
	} catch {
		return null;
	}
}

export type PeopleEntityProfileRelayProps = {
	accountId: string;
	cloudId: string;
	orgId: string;
	requestingUserId: string;
	sourceProduct?: string;
};

const DEFAULT_COLLAB_GRAPH_MODEL = 'cg_user_user_v4_';
type CollabGraphModel = 'cg_user_user_v4_' | 'cg_user_user_v5' | 'cg_user_user_v5_regression';

// Reads the current gate/experiment state fresh on every call. This is a cheap, synchronous
// lookup, so it is called directly during render rather than memoized: memoizing it (e.g. via
// `useMemo` with an empty dependency array) would freeze the result for a component's entire
// lifetime and never reflect a gate/experiment change made after the component first mounted.
const getCollabGraphExperimentModel = (): CollabGraphModel => {
	if (fg('cg_sain_user_user_v5')) {
		return 'cg_user_user_v5_regression';
	}

	return FeatureGates.getExperiment('people-card-collaboration-graph-v5').get(
		'featureServiceModel',
		DEFAULT_COLLAB_GRAPH_MODEL,
	);
};

/**
 * Loads profile, recent work, smarts, and reporting lines via one Relay query
 */
export function PeopleEntityProfileRelay({
	accountId,
	cloudId,
	orgId,
	requestingUserId,
	sourceProduct,
}: PeopleEntityProfileRelayProps): React.JSX.Element {
	const accountIdARI = `ari:cloud:identity::user/${accountId}`;
	const featureServiceModel = getCollabGraphExperimentModel();

	const data = useLazyLoadQuery<peopleEntityProfileRelayQuery>(
		graphql`
			query peopleEntityProfileRelayQuery(
				$first: Int!
				$filter: ActivityFilter
				$recommendationsQuery: SmartsRecommendationsQuery!
				$reportingLinesAaid: String!
				$reportingLinesOrgId: String!
				$reportingLinesSiteId: String!
				$singleUserAccountId: String!
				$singleUserRequestingUserId: String!
				$singleUserTenantId: String!
			) {
				activity {
					workedOn(first: $first, filter: $filter) {
						...luminaUserProfilePreviewRelay_workedOn
					}
				}
				smarts {
					...luminaUserProfilePreviewRelay_smarts
						@arguments(recommendationsQuery: $recommendationsQuery)
				}
				knowledgeDiscovery {
					...luminaUserProfilePreviewRelay_user
						@arguments(
							accountId: $singleUserAccountId
							requestingUserId: $singleUserRequestingUserId
							tenantId: $singleUserTenantId
						)
					...luminaUserProfilePreviewRelay_reportingLines
						@arguments(
							aaid: $reportingLinesAaid
							orgId: $reportingLinesOrgId
							siteId: $reportingLinesSiteId
						)
				}
			}
		`,
		{
			first: 50,
			singleUserAccountId: accountId,
			singleUserRequestingUserId: requestingUserId,
			singleUserTenantId: cloudId,
			filter: { actors: [accountIdARI], collabContextId: buildCollabContextId(cloudId) },
			recommendationsQuery: {
				context: {
					userId: accountId,
					tenantId: cloudId,
				},
				modelRequestParams: {
					caller: 'lumina',
					experience: 'CgUserNearbyUser',
					featureServiceModel,
				},
				requestingUserId,
				maxNumberOfResults: 8,
			},
			reportingLinesAaid: accountId,
			reportingLinesOrgId: orgId,
			reportingLinesSiteId: buildSiteAri(cloudId, sourceProduct),
		},
	);
	return (
		<UserProfilePreviewRelay
			userFragment={data.knowledgeDiscovery ?? undefined}
			workedOnFragment={data.activity?.workedOn ?? undefined}
			smartsFragment={data.smarts ?? undefined}
			reportingLinesFragment={data.knowledgeDiscovery ?? undefined}
			cloudId={cloudId}
			orgId={orgId}
			siteId={buildSiteAri(cloudId, sourceProduct)}
			requestingUserId={requestingUserId}
			testId="lumina-people-entity-profile"
		/>
	);
}
